<template>
  <b-card class="p-0 campaign-job-card" body-class="px-3" footer-class="bg-primary-a1 border-0 p-2">
    <div class="d-flex flex-row justify-content-start align-items-start pb-4">
      <h3 class="flex-fill m-0 w-100" v-b-modal="`description-${job.id}`">
        {{ job.title }}
      </h3>
    </div>

    <key-stats class="w-100" :job="job"/>

    <template v-slot:footer>
      <div class="d-flex flex-row justify-content-start align-items-center">
        <div class="d-flex flex-column flex-md-row justify-content-start">
          <b-btn v-if="job.description" size="sm" variant="link" v-b-modal="`description-${job.id}`">{{ $t('campaign.buttons.read-description') }}</b-btn>
          <b-btn v-if="isRecruiter" size="sm" variant="link" :to="{ name: 'CampaignJobShow', params: { jobID: job.id } }">{{ $t('campaign.buttons.share-page') }}</b-btn>
          <b-btn v-if="isRecruiter" class="text-left" size="sm" :to="{ name: 'JobEdit', params: { jobID: job.id } }" title="Edit" variant="link">
            <font-awesome-icon icon="pencil-alt"/>
          </b-btn>
        </div>
        <div class="flex-fill"/>
        <div class="d-flex flex-column flex-md-row justify-content-start">
          <b-btn v-if="showLinkedInSearchOnJobs" v-b-modal="`job-linkedinSearch-${job.id}`" class="mr-md-2" variant="linkedin">
            <font-awesome-icon class="mr-2" :icon="['fab', 'linkedin']"/> {{ $t('jobs.labels.matches') }}
          </b-btn>
          <b-btn class="mt-1 mt-md-0 mr-md-2" v-if="canEmployeeApply" :disabled="!absoluteShareURL"
            :href="absoluteShareURL" target="_blank" variant="primary">{{ $t('campaign.buttons.apply') }}</b-btn>
          <b-btn class="mt-1 mt-md-0" variant="primary" v-b-modal="`job-actions-${job.id}`">{{ $t('jobs.ctas.item-employee') }}</b-btn>
        </div>
      </div>
    </template>

    <b-modal :id="`description-${job.id}`" :title="job.title" hide-footer lazy size="lg">
      <key-stats class="mb-3 w-100" :job="job"/>
      <people class="mb-3 w-100" :job="job"/>
      <markdown :source="job.description" :anchorAttributes="markdownAnchorAttributes"/>
    </b-modal>

    <b-modal :id="`job-linkedinSearch-${job.id}`" :title="$t('campaign.search-linkedin.title')" hide-footer lazy v-if="showLinkedInSearchOnJobs">
      <linkedin-search-details :jobID="job.id" :jobTitle="job.title" :shareUrl="absoluteShareURL"
        :linkedInSearchURL="(job.customFieldsHash ? job.customFieldsHash.linkedInSearchURL : null) || job.linkedInSearchURL" :preview-text="jobShareText" :preview-args="{ jobTitle: job.title }"
        @trackedLiBrowseEvent="trackedLiBrowseEvent()"
        @trackShareEvent="({ channel }) => trackJobSharedEvent({ channel })"
      />
    </b-modal>
    <b-modal :id="`job-actions-${job.id}`" header-class="border-0" hide-footer lazy>
      <job-actions-modal :job="job" :modalID="`job-actions-${job.id}`"/>
    </b-modal>

    <!-- Connect LinkedIn Modal -->
    <b-modal v-if="hasJobShareV2Enabled" title="Connect LinkedIn" :id="`connect-jobs-linkedin-${job.id}`" hide-footer @close="cleanupRoute()">
      <ConnectLinkedIn
        :entryID="job.id"
      />
    </b-modal>

    <!-- LinkedIn Post Editor Modal -->
    <b-modal v-if="hasJobShareV2Enabled" :id="`job-share-linkedin-post-editor-${job.id}`" hide-footer title="Edit LinkedIn Post" no-close-on-backdrop no-close-on-esc @close="cleanupRoute()">
      <LinkedInJobsPostEditor
        :job="{...job, url: absoluteShareURL}"
        :campaignID="job && job.campaign && job.campaign.id"
        @track="({ channel }) => trackJobSharedEvent({ channel })"
      />
    </b-modal>
  </b-card>
</template>

<script>
import KeyStats from '@src/components/jobs/key-stats';
import People from '@src/components/jobs/people';
import Markdown from 'vue-markdown';
import { mapRoot, mapPreferences } from '@src/mixins/root';
import LinkedinSearchDetails from '@src/components/jobs/LinkedInSearch';
import JobActionsModal from '@src/modals/job-actions-modal';
import CampaignBadge from '@/components/campaign/Badge';
import LinkedInJobsPostEditor from '@/components/content-share/LinkedInJobsPostEditor';
import ConnectLinkedIn from '@/components/content-share/ConnectLinkedIn';

export default {
  components: {
    KeyStats,
    People,
    Markdown,
    LinkedinSearchDetails,
    CampaignBadge,
    JobActionsModal,
    LinkedInJobsPostEditor,
    ConnectLinkedIn
  },
  props: {
    job: {
      type: Object,
      required: true,
    },
  },
  async mounted() {
    const { state } = this.user || {};
    const { linkedIn } = state || {};
    const { accessToken, expiresAt, version } = linkedIn || {};
    const now = new Date().getTime();
    const isLinkedInConnected = accessToken && expiresAt && expiresAt > now && version === 'v1';

    if (this.$route.query && this.$route.query.j && this.$route.query.j === this.job.id) {
      if (isLinkedInConnected) {
        this.$bvModal.show(`job-share-linkedin-post-editor-${this.$route.query.j}`);
      } else {
        this.$bvModal.show(`connect-jobs-linkedin-${this.$route.query.j}`);
      }
    }

    if (this.$route.query && this.$route.query.callback && !this.$route.hash.includes('error')) {
      const jobID = this.$route.query.callback.split(':')[1];

      if (jobID && jobID === this.job.id) {
        this.$bvModal.show(`job-share-linkedin-post-editor-${jobID}`);
      }
    }
  },
  computed: {
    ...mapRoot([ 'isRecruiter', 'origin', 'isAssurant', 'isHelloFresh', 'user' ]),
    ...mapPreferences([ 'showLinkedInSearchOnJobs', 'canEmployeeApply', 'featuresEnabled' ]),
    ...mapRoot({
      canShareJobsWith({ company }) {
        const { preferences, sitePreferences, sharePreferences } = company || {};
        const { canShareJobsWith } = { ...preferences, ...sitePreferences, ...sharePreferences };
        return canShareJobsWith;
      },
      jobShareText({ company }) {
        const { preferences, sitePreferences, ...sharePreferences } = company || {};
        const { preferences: eventPreferences } = this.event || {};
        const { customFieldsHash: customFields } = this.job || {};
        const { jobShareText } = { ...preferences, ...sitePreferences, ...sharePreferences, ...eventPreferences, ...customFields };

        if (this.isAssurant && this.user && this.user.locale === 'fr') {
          return 'Assurant recrute ! Nous recherchons un(e) {jobTitle} pour rejoindre notre équipe. Consultez l’annonce et postulez maintenant !';
        }

        if (this.isHelloFresh && this.user && this.user.locale === 'de') {
          return 'Hast du Lust mit mir bei HelloFresh als {jobTitle} zu arbeiten? Jetzt bewerben!';
        }

        return jobShareText || 'Are you interested in working with me as a {jobTitle}?';
      },
      hasJobShareV2Enabled() {
        return (this.featuresEnabled || []).includes('job-share-v2');
      },
    }),
    absoluteShareURL() {
      const { origin } = this;
      const { id: campaignID } = this.campaign || {};
      const { id: eventID } = this.event || {};
      const { shareCode } = this.job || {};

      if (origin && shareCode) {
        let prefix = null;

        if (campaignID) {
          prefix = `/c/r/${campaignID}/`;
        } else if (eventID) {
          prefix = `/e/r/${eventID}/`;
        } else {
          prefix = '/r/';
        }

        return `${this.origin}${prefix}${shareCode}`;
      } else {
        return null;
      }
    },
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    referForm() {
      const form = {
        fullName: {
          label: this.$t('jobs.share-modal.labels.contactName'),
          placeholder: this.$t('jobs.share-modal.placeholders.contactName'),
          required: true,
          type: 'STRING',
        },
        emailAddress: {
          label: this.$t('jobs.share-modal.labels.emailAddress'),
          placeholder: this.$t('jobs.share-modal.placeholders.emailAddress'),
          required: true,
          type: 'STRING',
          options: {
            autocomplete: 'off',
          },
          validation: {
            isEmailAddress: true,
          },
        },
      };

      return {
        autocomplete: 'off',
        layout: 'inline-md-3',
        submitLabel: 'Refer',
        form,
      };
    },
  },
  methods: {
    async trackedLiBrowseEvent() {
      try {
        const { id: jobID, campaign } = this.job;
        const { id: campaignID } = campaign || {};

        await this.$root.captureLog({
          eventName: 'JOB_BROWSE_LINKEDIN',
          campaign: { id: campaignID },
          job: { id: jobID },
          user: { self: true },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    async trackJobSharedEvent({ channel }) {
      try {
        const { id: jobID, campaign } = this.job;
        const { id: campaignID } = campaign || {};
        await this.$root.captureLog({
          eventName: 'JOB_SHARED',
          campaign: { id: campaignID },
          job: { id: jobID },
          user: { self: true },
          dataHash: { channel },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    cleanupRoute() {
      if (this.$router.history && this.$router.history.current && this.$router.history.current.query) {
        // refresh the page if we open the card from a comm message
        if (this.$router.history.current.query.j || (this.$router.history.current.query.callback && this.$router.history.current.query.callback.startsWith('connectLinkedInForPosting'))) {
          this.$router.push(this.$router.history.current.path);
          location.reload();
        } else {
          this.$router.push(this.$router.history.current.path);
        }
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign/job-card.vue